import { useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./index.module.css";
import { LocationContext } from "../../../Contexts/LocationContext";
import { useJsApiLoader } from "@react-google-maps/api";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_KEY;
const libraries = ["places"];

export default function MobileLocationPage() {
  const navigate = useNavigate();
  const { location, setLocation } = useContext(LocationContext);
  
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [placeholder, setPlaceholder] = useState("Search for a location...");
  
  const autocompleteServiceRef = useRef(null);
  const geocoderRef = useRef(null);

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_API_KEY,
    libraries: libraries,
  });

  // Initialize Google Places services
  useEffect(() => {
    if (isLoaded) {
      autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
      geocoderRef.current = new window.google.maps.Geocoder();
      console.log('✅ Google Places API loaded');
    }
  }, [isLoaded]);

  // Get place details and extract location info
  const getPlaceDetails = async (placeId) => {
    if (!geocoderRef.current) return;

    try {
      const result = await geocoderRef.current.geocode({ placeId });
      
      if (result.results && result.results[0]) {
        const place = result.results[0];
        const addressComponents = place.address_components;
        
        // Extract location data
        let city = "";
        let district = "";
        let state = "";
        let country = "";
        let pincode = "";
        
        addressComponents.forEach(component => {
          const types = component.types;
          
          if (types.includes("locality")) {
            city = component.long_name;
          } else if (types.includes("administrative_area_level_2")) {
            district = component.long_name;
          } else if (types.includes("administrative_area_level_1")) {
            state = component.long_name;
          } else if (types.includes("country")) {
            country = component.long_name;
          } else if (types.includes("postal_code")) {
            pincode = component.long_name;
          }
        });

        const updatedLocation = {
          fullAddress: place.formatted_address,
          city: city || district || "",
          district: district || "",
          state: state,
          country: country,
          pincode: pincode,
          latitude: place.geometry.location.lat(),
          longitude: place.geometry.location.lng(),
        };

        console.log('📍 Location selected:', updatedLocation);
        
        // Update placeholder with selected location
        const locationDisplay = updatedLocation.city && updatedLocation.district
          ? `${updatedLocation.city}, ${updatedLocation.district}`
          : updatedLocation.city || updatedLocation.district || "Location selected";
        
        setPlaceholder(locationDisplay);
        
        // Save to context and navigate after a brief delay
        setLocation(updatedLocation);
        
        setTimeout(() => {
          navigate("/");
        }, 800);
      }
    } catch (err) {
      console.error("❌ Error getting place details:", err);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (placeId) => {
    getPlaceDetails(placeId);
    setSuggestions([]);
    setSearchQuery("");
  };

  // Fetch suggestions from Google Places
  useEffect(() => {
    if (!searchQuery.trim() || !autocompleteServiceRef.current) {
      setSuggestions([]);
      return;
    }

    const fetchSuggestions = () => {
      const request = {
        input: searchQuery,
        componentRestrictions: { country: 'in' },
      };

      console.log('🔍 Searching for:', searchQuery);

      autocompleteServiceRef.current.getPlacePredictions(
        request,
        (predictions, status) => {
          console.log('📍 API Status:', status);
          console.log('📍 Predictions:', predictions);
          
          if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
            console.log('✅ Found predictions:', predictions.length);
            setSuggestions(predictions);
          } else {
            console.log('⚠️ No predictions or error:', status);
            setSuggestions([]);
          }
        }
      );
    };

    const debounceTimer = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // Update placeholder based on current location
  useEffect(() => {
    if (location) {
      const { city, district } = location;
      if (city && district) {
        setPlaceholder(`${city}, ${district}`);
      } else if (city) {
        setPlaceholder(city);
      }
    }
  }, [location]);

  if (!isLoaded) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Loading location services...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.header}>
          <h1 className={styles.title}>Select Your Location</h1>
          <p className={styles.subtitle}>Choose your area for personalized experience</p>
        </div>

        <div className={styles.searchSection}>
          <div className={styles.searchWrapper}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              className={styles.input}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={placeholder}
              autoComplete="off"
            />
            {searchQuery && (
              <button 
                className={styles.clearButton} 
                onClick={() => {
                  setSearchQuery("");
                  setSuggestions([]);
                }}
              >
                ✕
              </button>
            )}
          </div>

          {/* Debug info */}
          {searchQuery && (
            <div style={{ padding: '10px', fontSize: '12px', color: '#666', textAlign: 'center' }}>
              Searching for: "{searchQuery}" - Found: {suggestions.length} results
            </div>
          )}

          {suggestions.length > 0 && (
            <ul className={styles.suggestions}>
              {suggestions.map((suggestion) => (
                <li
                  key={suggestion.place_id}
                  className={styles.suggestionItem}
                  onClick={() => handleSuggestionClick(suggestion.place_id)}
                >
                  <span className={styles.suggestionIcon}>📍</span>
                  <div className={styles.suggestionText}>
                    <div className={styles.mainText}>
                      {suggestion.structured_formatting.main_text}
                    </div>
                    <div className={styles.secondaryText}>
                      {suggestion.structured_formatting.secondary_text}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {/* Show message if no results */}
          {searchQuery && suggestions.length === 0 && autocompleteServiceRef.current && (
            <div className={styles.noResults}>
              <p>No locations found. Try searching for:</p>
              <p>• Malappuram</p>
              <p>• Kochi</p>
              <p>• Thiruvananthapuram</p>
            </div>
          )}
        </div>

        {!searchQuery && (
          <div className={styles.placeholder}>
            <div className={styles.placeholderIcon}>🗺️</div>
            <p className={styles.placeholderTitle}>Search for Your Location</p>
            <p className={styles.placeholderSubtext}>
              Start typing your city or area name
            </p>
            <div className={styles.exampleTags}>
              <span className={styles.tag}>Malappuram</span>
              <span className={styles.tag}>Kochi</span>
              <span className={styles.tag}>Thiruvananthapuram</span>
            </div>
          </div>
        )}

        <div className={styles.actions}>
          <button className={styles.skipButton} onClick={() => navigate("/")}>
            Skip for now
          </button>
        </div>
      </div>
    </div>
  );
}